import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import type { RoomHub } from '../net/roomHub';
import type { ArrowMode, LineStyle } from '../net/wire';
import type { ToolMode } from '../possession/ToolMode';
import { orientBetween } from '../room/NodeMeshFactory';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';
import type { Grabber, GrabberHub } from './Grabbers';

/**
 * Drawing an edge: aim at a node, pull the trigger, drag to another node, let go. Link mode's
 * own gesture (plan/mode-hierarchy.md) — the dominant trigger only ever means this while Link
 * is the current mode (`tick()`'s own gate, `toolMode.isLinkMode`), the same way it only ever
 * means "grab" in Build mode (`PossessionManager.considerGrab`'s `isToolActive` guard).
 *
 * Deliberately a gesture rather than a menu step. Connections are the substance of a diagram
 * and get made constantly, so the cost of making one has to be near zero — and dragging from
 * one thing to another is what people already do on a whiteboard.
 *
 * **Grabbing and linking used to compete for the identical trigger press within one mode**
 * (`3fb3a60`, "Grabbing moves from the dominant grip to the dominant trigger; near/far is
 * gone"), disambiguated only by proximity (`GrabReach.nodeWithinReach`) as an interim measure —
 * a deliberate stopgap, shipped ahead of Link mode existing, because the owner wanted the
 * trigger-grab change tested in isolation first (plan/mode-hierarchy.md's own "Sequencing"
 * section records the reversal). **That stopgap is retired here.** Grabbing and linking are now
 * never live in the same mode at once: Build's trigger only ever grabs (`ConnectionDrawer.tick`
 * never runs there any more — see the gate below), and Link's trigger only ever links
 * (`PossessionManager.considerGrab` already refuses a grab in any non-build mode via
 * `ToolMode.isToolActive`, unchanged by this). With the two gestures structurally confined to
 * disjoint modes, a proximity tie-break has nothing left to arbitrate — removed along with its
 * import of `GrabReach.nodeWithinReach`, which this file no longer references at all.
 *
 * The current line style/arrow (`ToolMode.linkLineSelection`/`linkArrowMode`) apply to whatever
 * gets drawn or re-drawn on release — see `submit()`. Re-linking an already-connected pair with
 * the style cycled to `'remove'` deletes the connection between them instead of restyling it;
 * re-linking in the opposite direction from the one already stored reverses it, applying the
 * current style/arrow at the same time.
 */

/**
 * How far along the aim ray a node must be before a *hand* counts as aiming at it rather
 * than reaching for it. Controllers say which they meant, so they only need enough clearance
 * to keep a node directly at the muzzle from being ambiguous.
 */
const MIN_AIM_DISTANCE = 0.25;
const MIN_AIM_DISTANCE_EXPLICIT = 0.05;

const MAX_AIM_DISTANCE = 12;

/** Rays are thin; a little forgiveness makes aiming at a 30cm shape practical. */
const AIM_TOLERANCE = 0.12;

const PREVIEW_COLOR = '#FFD166';

/** How far from an edge the aim can be and still count as pointing at it. */
const EDGE_AIM_TOLERANCE = 0.09;

/**
 * Samples taken along an edge when testing whether the aim is on it.
 *
 * Sampling rather than a closed-form ray-to-segment distance: a diagram has tens of edges, not
 * thousands, and the arithmetic here is read far more often than it runs.
 */
const EDGE_SAMPLES = 14;

// spec: SPEC-087
// spec: SPEC-089
export class ConnectionDrawer {
  private readonly preview: Mesh;

  private fromNodeId: string | null = null;
  private grabberId: string | null = null;

  /** Set while the create call is in flight, so one drag cannot make two edges. */
  private submitting = false;

  onRefused: ((reason: string) => void) | null = null;

  /**
   * An edge was aimed at, or the aim found nothing and the selection should clear.
   *
   * Selection lives here because it is the same gesture: the dominant trigger on a node draws
   * from it, on an edge selects it, on nothing clears. One binding, and what it hits decides.
   */
  onEdgeAimed: ((connectionId: string | null) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly hub: RoomHub,

    // Read directly, the same sibling-read shape PossessionManager also uses it with: this
    // gesture only runs while `toolMode.isLinkMode` (`tick()`'s own gate, see this class's own
    // module doc). AuthoringController.arbitrate also cancels a drag already in progress the
    // instant the mode stops being link, for whichever mode it becomes instead; this is what
    // stops a fresh one from starting in the first place.
    private readonly toolMode: ToolMode,
  ) {
    this.preview = CreateCylinder('edge-preview', { height: 1, diameter: 0.02, tessellation: 6 }, scene);

    const material = new StandardMaterial('edge-preview', scene);
    const color = Color3.FromHexString(PREVIEW_COLOR);
    material.diffuseColor = color;
    material.emissiveColor = color.scale(0.6);
    material.specularColor = Color3.Black();

    this.preview.material = material;
    this.preview.isPickable = false;
    this.preview.rotationQuaternion = Quaternion.Identity();
    this.preview.setEnabled(false);
  }

  get isDrawing(): boolean {
    return this.fromNodeId !== null;
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /** Cancels any drag in progress. Used when another gesture takes priority. */
  cancel(): void {
    this.fromNodeId = null;
    this.grabberId = null;
    this.preview.setEnabled(false);
  }

  private tick(): void {
    const grabbers = this.grabbers.grabbers;

    if (this.fromNodeId !== null) {
      this.continueDrag(grabbers);
      return;
    }

    // This gesture belongs to Link mode only, now — every other mode's dominant trigger means
    // something else entirely (a grab in Build, a draw/scan action elsewhere), and the mode
    // hierarchy is what disambiguates a press, not proximity (see this file's own module doc).
    // `submitting` still guards against a slow in-flight request letting a second drag start.
    if (this.submitting || !this.toolMode.isLinkMode) {
      return;
    }

    // Dominant hand only, on the trigger.
    for (const grabber of grabbers) {
      if (grabber.role !== 'dominant' || !grabber.far.justPressed) {
        continue;
      }

      const aimed = this.nodeUnderAim(grabber);

      if (aimed !== null) {
        this.fromNodeId = aimed.nodeId;
        this.grabberId = grabber.id;
        this.preview.setEnabled(true);
        return;
      }

      // Not a node. An edge under the aim gets selected; nothing under it clears whatever was
      // selected, which is what makes "point somewhere else" the way out.
      this.onEdgeAimed?.(this.edgeUnderAim(grabber));
      return;
    }
  }

  /**
   * Nearest edge along the aim ray, by sampling points along each edge.
   *
   * Endpoints come from the meshes rather than the DTOs for the same reason the renderer's do:
   * a held node has moved since its last durable write, and an edge you can see should be an
   * edge you can point at.
   */
  private edgeUnderAim(grabber: Grabber): string | null {
    let bestId: string | null = null;
    let bestAlong = Number.POSITIVE_INFINITY;

    // Same branch `nodeUnderAim` uses, and for the same reason — reached from the identical
    // dominant-trigger press (`tick`, `:169`/`:224`), so the two must not disagree about how
    // close counts as "aiming" rather than "reaching". This used to hardcode
    // MIN_AIM_DISTANCE_EXPLICIT unconditionally, which agrees with `nodeUnderAim` for every
    // controller profile this project ships (all of them set `separatesNearFar`) and is latent
    // rather than live today — see this file's own module doc and CLAUDE.md's own note on this
    // fix — but would disagree the instant a squeeze-less controller (or, historically, a
    // tracked hand) made `separatesNearFar` false: a press at 0.15m would refuse a node but
    // happily select an edge there.
    const minimum = grabber.separatesNearFar ? MIN_AIM_DISTANCE_EXPLICIT : MIN_AIM_DISTANCE;

    for (const connection of this.state.connections.values()) {
      const from = this.view.meshFor(connection.fromNodeId)?.position;
      const to = this.view.meshFor(connection.toNodeId)?.position;

      if (!from || !to) {
        continue;
      }

      for (let step = 0; step <= EDGE_SAMPLES; step += 1) {
        const point = Vector3.Lerp(from, to, step / EDGE_SAMPLES);
        const along = Vector3.Dot(point.subtract(grabber.aimOrigin), grabber.forward);

        if (along < minimum || along > MAX_AIM_DISTANCE || along >= bestAlong) {
          continue;
        }

        const onRay = grabber.aimOrigin.add(grabber.forward.scale(along));

        if (Vector3.Distance(onRay, point) > EDGE_AIM_TOLERANCE) {
          continue;
        }

        bestAlong = along;
        bestId = connection.id;
      }
    }

    return bestId;
  }

  private continueDrag(grabbers: Grabber[]): void {
    const grabber = grabbers.find((candidate) => candidate.id === this.grabberId);
    const fromMesh = this.fromNodeId === null ? undefined : this.view.meshFor(this.fromNodeId);

    if (!grabber || !fromMesh) {
      this.cancel();
      return;
    }

    const target = this.nodeUnderAim(grabber);

    // Snaps to a node when one is aimed at, so the user sees the edge commit before letting
    // go rather than guessing whether it landed.
    const endPoint =
      target !== null && target.nodeId !== this.fromNodeId
        ? (this.view.meshFor(target.nodeId)?.position ?? target.point)
        : grabber.aimOrigin.add(grabber.forward.scale(1.2));

    orientBetween(this.preview, fromMesh.position, endPoint);

    if (!grabber.far.justReleased) {
      return;
    }

    const from = this.fromNodeId;
    this.cancel();

    if (target === null || from === null || target.nodeId === from) {
      // Released over nothing, which is the cancel gesture.
      return;
    }

    void this.submit(from, target.nodeId);
  }

  /**
   * Creates a link, or — landing on an already-connected pair — restyles, reverses or removes
   * the existing one instead. plan/mode-hierarchy.md: "Re-linking two already-linked nodes with
   * the style cycled to 'none' removes the connection" — read as `'remove'` here, the UI-only
   * selector value `ToolMode.linkLineSelection` can carry but a `LineStyle` never does.
   *
   * A connection "between" two nodes is found regardless of which one the drag started from —
   * the same edge reads the same way from either end, so re-linking B -> A against an existing
   * A -> B is a re-link of *that* edge, not a second, parallel one.
   */
  private async submit(fromNodeId: string, toNodeId: string): Promise<void> {
    const existing = [...this.state.connections.values()].find(
      (connection) =>
        (connection.fromNodeId === fromNodeId && connection.toNodeId === toNodeId) ||
        (connection.fromNodeId === toNodeId && connection.toNodeId === fromNodeId),
    );

    const line = this.toolMode.linkLineSelection;

    if (existing) {
      if (line === 'remove') {
        await this.removeExisting(existing.id);
        return;
      }

      // Dragging from the endpoint that is not today's `fromNodeId` re-links it the other way
      // round — the drag direction itself is how "reverse" is expressed now, superseding the
      // tray's own former `reverse` action (plan/mode-hierarchy.md: "superseded by Link mode's
      // own dominant-stick controls for reverse/style/delete").
      const reversed = existing.fromNodeId === toNodeId && existing.toNodeId === fromNodeId;
      await this.updateExisting(existing.id, line, this.toolMode.linkArrowMode, reversed);
      return;
    }

    if (line === 'remove') {
      // Nothing between these two to remove — refuse rather than silently doing nothing, the
      // same "honest failure" standard every other refusal in this class already holds to.
      this.onRefused?.('nothing to remove there');
      return;
    }

    this.submitting = true;

    try {
      const created = await this.hub.createConnection({
        connectionId: crypto.randomUUID(),
        fromNodeId,
        toNodeId,
        style: { color: null, arrow: this.toolMode.linkArrowMode, line },
      });

      // Applied here because the hub broadcasts ConnectionCreated to *others* only — the
      // caller is handed the new state as a return value instead. Dropping it was the rest of
      // "connections don't stick": the edge was written, the other peers saw it, and the
      // person who drew it saw nothing until they reloaded.
      this.state.upsertConnection(created);
    } catch (error) {
      this.onRefused?.(error instanceof Error ? error.message : String(error));
    } finally {
      this.submitting = false;
    }
  }

  private async removeExisting(connectionId: string): Promise<void> {
    this.submitting = true;

    try {
      await this.hub.deleteConnection(connectionId);
      this.state.removeConnection(connectionId);
    } catch (error) {
      this.onRefused?.(error instanceof Error ? error.message : String(error));
    } finally {
      this.submitting = false;
    }
  }

  private async updateExisting(
    connectionId: string,
    line: LineStyle,
    arrow: ArrowMode,
    reverse: boolean,
  ): Promise<void> {
    this.submitting = true;

    try {
      // Same "return value is the only thing that reaches the caller" shape createConnection
      // already needs — see the comment above.
      const updated = await this.hub.updateConnection({
        connectionId,
        style: { color: null, arrow, line },
        reverse,
      });

      this.state.upsertConnection(updated);
    } catch (error) {
      this.onRefused?.(error instanceof Error ? error.message : String(error));
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Nearest node along the grabber's aim ray.
   *
   * Distance from the ray rather than a mesh pick: a pick needs the ray to actually strike
   * geometry, which at arm's length against a 30cm shape is far fussier than the gesture
   * feels like it should be.
   */
  private nodeUnderAim(grabber: Grabber): { nodeId: string; point: Vector3 } | null {
    let bestId: string | null = null;
    let bestAlong = Number.POSITIVE_INFINITY;
    let bestPoint = Vector3.Zero();

    const minimum = grabber.separatesNearFar ? MIN_AIM_DISTANCE_EXPLICIT : MIN_AIM_DISTANCE;

    for (const nodeId of this.state.nodes.keys()) {
      const mesh = this.view.meshFor(nodeId);

      if (!mesh) {
        continue;
      }

      // Forced: this runs from onBeforeRenderObservable, and an unforced read here can return
      // last frame's world matrix regardless of registration order (CLAUDE.md, "An unforced
      // `computeWorldMatrix` can return last frame's answer"). Worse than the possession case
      // below — `bestPoint` is anchored to this stale `centerWorld` and handed onward, not just
      // compared against it.
      mesh.computeWorldMatrix(true);
      const bounding = mesh.getBoundingInfo().boundingSphere;
      const toCentre = bounding.centerWorld.subtract(grabber.aimOrigin);
      const along = Vector3.Dot(toCentre, grabber.forward);

      if (along < minimum || along > MAX_AIM_DISTANCE) {
        continue;
      }

      const closestOnRay = grabber.aimOrigin.add(grabber.forward.scale(along));
      const missBy = Vector3.Distance(closestOnRay, bounding.centerWorld);

      if (missBy > bounding.radiusWorld + AIM_TOLERANCE) {
        continue;
      }

      // Nearest along the ray wins, so a node in front occludes one behind it.
      if (along < bestAlong) {
        bestAlong = along;
        bestId = nodeId;
        bestPoint = bounding.centerWorld.clone();
      }
    }

    return bestId === null ? null : { nodeId: bestId, point: bestPoint };
  }

  dispose(): void {
    this.preview.material?.dispose();
    this.preview.dispose();
  }
}
